import React, { useState } from "react";
import {
  Heart,
  Briefcase,
  Clock,
  ShieldCheck,
  Sparkles,
  Utensils,
  Moon,
  X,
  TrendingUp,
  Megaphone,
} from "lucide-react";

const SupportCard = ({ icon: Icon, title, status, desc, color }) => (
  <div className="bg-white p-4 rounded-xl border border-stone-100 shadow-sm flex items-start gap-4">
    <div className={`p-2 rounded-full ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h4 className="font-bold text-stone-800 text-sm">{title}</h4>
      <p className="text-xs text-stone-500 mt-1 leading-relaxed">{desc}</p>
      <div className="mt-2 text-[10px] font-bold uppercase tracking-widest opacity-60">
        Status: {status}
      </div>
    </div>
  </div>
);

const MelyssaDashboard = () => {
  const [showUpdate, setShowUpdate] = useState(true);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* 🟢 LIVE STATUS POP-UP (The Win) */}
      {showUpdate && (
        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-5 rounded-r-xl shadow-sm flex justify-between items-start animate-in slide-in-from-top-2 relative">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-emerald-200 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Note to Self • 10:55 AM Mon
              </span>
            </div>
            <h3 className="text-lg font-bold text-emerald-900 mb-1">
              ✨ Massive Win: The "Boss Call"
            </h3>
            <p className="text-emerald-800 text-sm leading-relaxed max-w-2xl">
              Her new boss is <strong>"super impressed."</strong>
              <br />
              <br />
              <span className="font-semibold">The Shift:</span> She can finally
              unclench. The stress is gone. We are fully aligned on the post-New
              Year's return.
            </p>
          </div>
          <button
            onClick={() => setShowUpdate(false)}
            className="text-emerald-400 hover:text-emerald-700 bg-emerald-100 hover:bg-emerald-200 p-1 rounded-full transition-colors absolute top-4 right-4"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 1. EMPATHY HEADER */}
      <div className="bg-gradient-to-r from-rose-50 to-indigo-50 p-6 rounded-2xl border border-rose-100 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Heart className="w-6 h-6 text-rose-500 fill-rose-500" />
            Priority: Melyssa
          </h2>
          <p className="text-stone-600 mt-1 max-w-lg text-sm leading-relaxed">
            She is carrying the heavy professional load this week.{" "}
            <strong>My Goal:</strong> Run Ops & Logistics perfectly so she has
            the quiet/space to finish strong.
          </p>
        </div>
        <div className="hidden md:block">
          <div className="bg-white/60 p-4 rounded-xl border border-white/50 text-center">
            <div className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">
              Current State
            </div>
            <div className="font-serif text-lg text-stone-800 italic">
              "Feeling good at work"
            </div>
          </div>
        </div>
      </div>

      {/* 2. HER TIMELINE (Understanding Her Constraints) */}
      <div>
        <h3 className="text-sm font-bold text-stone-400 uppercase tracking-widest mb-4">
          Her Context & My Moves
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* MONDAY */}
          <div className="bg-white p-4 rounded-xl border-l-4 border-emerald-400 shadow-sm">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-stone-800">Monday</span>
              <Briefcase className="w-4 h-4 text-emerald-500" />
            </div>
            <p className="text-xs text-stone-500 mb-3">
              Focus: Deep Work {"->"} Validation received.
            </p>
            <div className="bg-emerald-50 text-emerald-700 text-xs p-2 rounded border border-emerald-100 flex items-center gap-2">
              <Sparkles className="w-3 h-3" />
              <span>
                <strong>My Move:</strong> Cleaners handled. House is quiet for
                her.
              </span>
            </div>
          </div>

          {/* TUESDAY */}
          <div className="bg-white p-4 rounded-xl border-l-4 border-rose-300 shadow-sm">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-stone-800">Tuesday</span>
              <Megaphone className="w-4 h-4 text-rose-500" />
            </div>

            <div className="space-y-2 mb-3">
              <p className="text-xs text-stone-600">
                <strong>1. Work Marathon</strong>
              </p>
              <p className="text-xs text-stone-600">
                <strong>2. "Team Mom" Duty:</strong> Hosting Cheer Party.
              </p>
              <p className="text-xs text-stone-600">
                <strong>3. Driving:</strong> Laura & Emma logistics.
              </p>
            </div>

            <div className="bg-rose-50 text-rose-700 text-xs p-2 rounded border border-rose-100 flex items-center gap-2">
              <Moon className="w-3 h-3" />
              <span>
                <strong>Reality:</strong> She gets to Arigna @ 9:30pm drained.
                Be ready.
              </span>
            </div>
          </div>

          {/* WEDNESDAY */}
          <div className="bg-white p-4 rounded-xl border-l-4 border-indigo-300 shadow-sm">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-stone-800">Wednesday</span>
              <Heart className="w-4 h-4 text-indigo-400" />
            </div>
            <p className="text-xs text-stone-500 mb-3">Transition to Rest.</p>
            <div className="bg-indigo-50 text-indigo-700 text-xs p-2 rounded border border-indigo-100 flex items-center gap-2">
              <Utensils className="w-3 h-3" />
              <span>
                <strong>My Move:</strong> Food handled (Conrad's). No cooking
                for her.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 3. PROTECTIVE ACTIONS */}
      <div>
        <h3 className="text-sm font-bold text-stone-400 uppercase tracking-widest mb-4">
          My Protection Plan
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SupportCard
            icon={Utensils}
            title="The Food Plan"
            color="bg-emerald-100 text-emerald-600"
            status="Solved"
            desc="I handled Mom regarding the menu. Melyssa won't have to explain 'Gluten Free' or worry about food on Xmas."
          />

          <SupportCard
            icon={ShieldCheck}
            title="Logistics Shield"
            color="bg-stone-100 text-stone-600"
            status="Active"
            desc="Cleaners done. Groceries done. Environment is set so she walks into a calm house."
          />

          <SupportCard
            icon={Briefcase}
            title="Emotional Pivot"
            color="bg-emerald-100 text-emerald-600"
            status="Validated & Secure"
            desc="Work is secure. My job now is to help her pivot from 'survival mode' to vacation mode."
          />
        </div>
      </div>
    </div>
  );
};

export default MelyssaDashboard;
